<template>
  <div class="d-flex flex-column">
    <div class="grid-header">
      <h1>{{ $t("borradores-operacion.agua.titulo") }}</h1>
      <div class="toolbar">
      </div>
    </div>

    <DxDataGrid v-bind="gridProperties" :data-source="gridSource" ref="grid" class="dx-card wide-card grid-borradores-operacion-energia">
      <DxToolbar>

        <DxItem v-bind="getResetFiltersGridBtn(() => grid)" />
        <DxItem v-bind="getResetStateGridBtn(() => grid)" />
        <DxItem v-bind="getColumnChooserBtn()" />
      </DxToolbar>
      <DxColumnFixing :enabled="true" />
      <DxColumn data-field="DireccionInmueble" data-type="string" :caption="$t('borradores-operacion.grid.direccion_inmueble')"></DxColumn>
      <DxColumn data-field="CodigoEstadoCalculado" :caption="$t('borradores-operacion.grid.codigo_estado_calculado')" cell-template="estadoTemplate"
        lookup-enabled="true">
        <DxLookup v-bind="getLookup('borradores-operacion', 'estado_calculado')"></DxLookup>
      </DxColumn>
      <DxColumn data-field="NombreCompletoCliente" data-type="string" :caption="$t('borradores-operacion.grid.nombre_completo_cliente')"></DxColumn>
      <DxColumn data-field="DocumentoCliente" data-type="string" :caption="$t('borradores-operacion.grid.documento_cliente')"></DxColumn>
      <DxColumn data-field="TelefonoCliente" data-type="string" :caption="$t('borradores-operacion.grid.telefono_cliente')"></DxColumn>
      <DxColumn data-field="EmailCliente" data-type="string" :caption="$t('borradores-operacion.grid.email_cliente')"></DxColumn>
      <DxColumn data-field="NombreEmpresaAgentes" data-type="string" :caption="$t('borradores-operacion.grid.nombre_empresa_agentes')"></DxColumn>
      <DxColumn data-field="AgenteCreador" data-type="string" :caption="$t('borradores-operacion.grid.agente_creador')"></DxColumn>
      <DxColumn data-field="FechaCreacion" data-type="date" :caption="$t('borradores-operacion.grid.fecha_creacion')" format="dd/MM/yyyy"></DxColumn>
      <DxColumn type="buttons" :caption="$t('borradores-operacion.grid.acciones')" :fixed="true" fixed-position="right" :buttons="[
        {
          cssClass: 'btn btn-link text-decoration-none btn-sm',
          text: $t('borradores-operacion.grid.ver_detalle'),
          icon: 'fa-regular fa-magnifying-glass text-success',
          onClick: (e: any) => verDetalle(e.row?.data.IdBorrador)
        },
        {
          cssClass: 'btn btn-link text-decoration-none btn-sm',
          text: $t('borradores-operacion.grid.validar_borrador'),
          icon: 'fa-regular fa-check text-success',
          onClick: (e: any) => validarClienteInmobiliario(e.row?.data.IdClienteInmobiliario, e.row?.data.RutaAutorizacionFirmada),
          disabled: (options: any) => options.row?.data.CodigoEstadoCliente != 'PDT_REVISION_CONSENTIMIENTO' ||
            options.row?.data.CodigoEstadoCalculado != 'PDT_PROCESAR'
        },
        {
          cssClass: 'btn btn-link text-decoration-none btn-sm',
          text: $t('borradores-operacion.grid.nueva_operacion'),
          icon: 'fa-regular fa-plus text-success',
          onClick: (e: any) => nuevaOperacion(e.row?.data.IdBorrador),
          disabled: (options: any) => options.row?.data.CodigoEstadoCalculado != 'PDT_PROCESAR' ||
            options.row?.data.CodigoEstadoCliente == 'PDT_REVISION_CONSENTIMIENTO'
        },
        {
          cssClass: 'btn btn-link text-decoration-none btn-sm',
          text: $t('borradores-operacion.grid.cancelar_borrador'),
          icon: 'fa-regular fa-trash text-success',
          onClick: (e: any) => cancelarBorrador(e.row?.data.IdBorrador),
          disabled: (options: any) => options.row?.data.CodigoEstadoCalculado != 'PDT_PROCESAR'
        }
      ]" />

      <template #booleanTemplate="{ data }">
        <BooleanTemplate :value="data.value" />
      </template>

      <template #estadoTemplate="{ data }">
        <div :class="[
          `d-inline-block rounded py-1 px-2 `,
          `estado-operacion ${data.value.replace(' ', '')}`,
        ]">
            {{ $t(`borradores-operacion.estado_calculado.${data.value}`) }}
        </div>
      </template>
    </DxDataGrid>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import { DxDataGrid, DxColumnFixing, DxColumn, DxLookup, DxToolbar, DxItem } from "devextreme-vue/data-grid";
import { getDefaultGridProperties, getColumnLookupBoolean, getLookup, getColumnChooserBtn, getResetFiltersGridBtn, getResetStateGridBtn, getDefaultGridWithStateStoringProperties } from "@helpers/grid-helper";
import { useApi } from "@api/integration";
import type CustomStore from "devextreme/data/custom_store";
import { type DXLoadOptions } from "@/.shared/.api";
import BooleanTemplate from "@/.shared/components/BooleanTemplate.vue";
import { useModalService } from "@/.shared/services/modal-service";
import { useIntl } from "@/.shared/services/i18n";
import BorradorOperacionDetailPanel from "@/components/borradores-operacion/BorradorOperacionDetailPanel.vue";
import { useRouter } from "@/.shared/services/routing-services";
import { useRefreshGridOnVisible } from "@/.shared/composables/useRefreshGridOnVisible";
import { messageBoxResult } from "@/.shared/components/dialogs/message-box";
import type { IDocumentInitialState } from "@/.shared/components/document-card/models/IDocumentCardProps";
import ValidarAutorizacionCliente from "@/components/borradores-operacion/ValidarAutorizacionCliente.vue";

const api = useApi();
const gridSource = ref<CustomStore>();
const grid = ref<DxDataGrid>();
const modal = useModalService();
const intl = useIntl();
const router = useRouter();
useRefreshGridOnVisible(grid);

onMounted(async () => {
  setDataSource();
});

const gridProperties = getDefaultGridWithStateStoringProperties("borradores-operacion-agua-grid-state.v1", () => grid.value);

const setDataSource = async () => {

  gridSource.value = api.apiStore({
    key: "IdBorrador",
    load: async (p) => {
      const q = p as DXLoadOptions;
      return await api.callStandardApi("getBorradoresOperacionAguaForGrid", { showLoading: false }, q)
    }
  });
}

const verDetalle = async (idBorrador: string) => {
  let title = `${intl.i18n.t("borradores-operacion.detalle.titulo")}`;
  const borradorModel = await api.callStandardApi("getBorradorOperacionDetail", { showLoading: true }, idBorrador);
  modal.showModal(
    BorradorOperacionDetailPanel,
    { model: borradorModel },
    { title: title, width: 800, height: 700 }
  );
}

const nuevaOperacion = async (idBorrador: string) => {
  const rutaBO = import.meta.env.VITE_BASE_URL;
  const url = `${rutaBO}/nueva-venta-agua?idBorrador=${idBorrador}`;
  window.open(url, '_blank');
}
const validarClienteInmobiliario = async (idClienteInmobiliario: string, ruta: string) => {
  let documentList: IDocumentInitialState[] = [];
  documentList.push({
    id: '1',
    uri: ruta,
    fileName: ruta.split('/').pop() ?? ''
  });

  const respuesta = await modal.showModal(ValidarAutorizacionCliente,
    { documentList: documentList, align: 'horizontal', title: '' },
    { title: intl.i18n.t('borradores-operacion.grid.validar_cliente_inmobiliario_titulo'), width: 550, height: 200 });

  if ((respuesta as any).result == messageBoxResult.yes) {
    await api.callStandardApi("validarAutorizacionClienteInmobiliario", { showLoading: true }, idClienteInmobiliario);
    grid.value?.instance?.refresh();
  }

  if ((respuesta as any).result == messageBoxResult.no) {

    const motivo = await modal.inputBox(intl.i18n.t('borradores-operacion.grid.cancelar_borrador_pregunta'), 'dxTextArea', null, true);

    if (motivo !== undefined) {
      await api.callStandardApi("rechazarAutorizacionClienteInmobiliario", { showLoading: true }, idClienteInmobiliario, motivo!);
      grid.value?.instance?.refresh();
    }
  }

}

const cancelarBorrador = async (idBorrador: string) => {
  const respuesta = await modal.inputBox(intl.i18n.t('borradores-operacion.grid.cancelar_borrador_pregunta'), 'dxTextArea', null, true);

  if (respuesta !== undefined) {
    await api.callStandardApi("cancelBorradorOperacion", { showLoading: true }, idBorrador, respuesta!);
    grid.value?.instance?.refresh();
  }
}

</script>
